import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { 
  WisdomIcon, ReflectIcon, InsightIcon, TranslateIcon, 
  LoadingIcon, ThumbsUpIcon, CopyIcon, CheckIcon, EnergyIcon,
  FeelIcon, ObserveIcon, NeedIcon
} from './BespokeIcons';
import { toast } from 'sonner';

/**
 * WisdomGuide - Compassionate guidance and suggestions for the ElchmE experience
 * 
 * Provides:
 * - Analysis of user input
 * - NVC translation suggestions
 * - Example phrases
 * - Feeling/need identification help
 */
const WisdomGuide = ({ 
  currentStep,
  currentInput = '',
  participants = [],
  onSuggestionApply,
  triggerClassName = ''
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [query, setQuery] = useState('');
  const [queryType, setQueryType] = useState('analyze');
  const [copied, setCopied] = useState(false);

  const queryTypes = [
    { id: 'analyze', label: 'Reflect', icon: ReflectIcon, description: 'Get feedback on your input' },
    { id: 'translate', label: 'NVC Translate', icon: TranslateIcon, description: 'Convert to NVC language' },
    { id: 'examples', label: 'Examples', icon: InsightIcon, description: 'See example phrases' },
    { id: 'identify', label: 'Identify', icon: EnergyIcon, description: 'Help find feelings/needs' },
  ];

  const stepContext = {
    0: { name: 'Setup', focus: 'situation' },
    1: { name: 'Observe', focus: 'observations', color: 'blue' },
    2: { name: 'Feel', focus: 'feelings', color: 'red' },
    3: { name: 'Need', focus: 'needs', color: 'emerald' },
    4: { name: 'Understand', focus: 'understanding', color: 'purple' },
    5: { name: 'Gold', focus: 'resolution', color: 'amber' },
  };

  const ctx = stepContext[currentStep] || stepContext[0];

  const handleQuery = async () => {
    setLoading(true);
    
    try {
      // Call backend wisdom endpoint
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wisdom/guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query_type: queryType,
          current_step: ctx.name,
          current_input: currentInput,
          user_query: query,
          participants: participants.map(p => p.name)
        })
      });

      if (!res.ok) {
        // Fallback to local suggestions if endpoint unavailable
        setResponse(getLocalSuggestion(queryType, ctx, currentInput, query));
      } else {
        const data = await res.json();
        setResponse(data);
      }
    } catch (error) {
      console.error('Wisdom Guide error:', error);
      // Use local fallback
      setResponse(getLocalSuggestion(queryType, ctx, currentInput, query));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const applySuggestion = (suggestion) => {
    onSuggestionApply?.(suggestion);
    toast.success('Suggestion applied!');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-1.5 text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10 ${triggerClassName}`}
          data-testid="wisdom-guide-trigger"
        >
          <WisdomIcon size={16} color="#FBBF24" />
          <span className="text-xs">Wisdom</span>
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-[#0f1419] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wisdom-guide-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 flex items-center justify-center">
              <WisdomIcon size={16} color="#FBBF24" />
            </div>
            Wisdom Guide
            <span className={`text-xs px-2 py-0.5 rounded-full bg-${ctx.color || 'slate'}-500/20 text-${ctx.color || 'slate'}-400 border border-${ctx.color || 'slate'}-500/30`}>
              {ctx.name} Step
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Query Type Selector */}
          <div className="grid grid-cols-4 gap-2">
            {queryTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => setQueryType(type.id)}
                  className={`p-2 rounded-lg border transition-all flex flex-col items-center gap-1 ${
                    queryType === type.id
                      ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                      : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                  }`}
                >
                  <Icon size={16} color={queryType === type.id ? '#FCD34D' : '#9CA3AF'} />
                  <span className="text-[10px]">{type.label}</span>
                </button>
              );
            })}
          </div>

          {/* Current Input Display */}
          {currentInput && (
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Your current input:</p>
              <p className="text-sm text-white/80">{currentInput.substring(0, 200)}{currentInput.length > 200 ? '...' : ''}</p>
            </div>
          )}

          {/* Optional Query Input */}
          <div>
            <label className="text-xs text-white/50 mb-1 block">Ask something specific (optional):</label>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g., How can I express this without blame?"
              className="bg-white/5 border-white/20 text-white placeholder:text-white/30 text-sm min-h-[60px]"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleQuery}
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-medium"
          >
            {loading ? (
              <><LoadingIcon size={16} color="#000000" className="mr-2" /> Reflecting...</>
            ) : (
              <><ReflectIcon size={16} color="#000000" className="mr-2" /> Get {queryTypes.find(t => t.id === queryType)?.label}</>
            )}
          </Button>

          {/* Response Display */}
          {response && (
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-lg p-4 border border-amber-500/20 space-y-3">
              <div className="flex items-start justify-between">
                <h4 className="text-amber-400 text-sm font-medium flex items-center gap-2">
                  <InsightIcon size={16} color="#FBBF24" />
                  {response.title || 'Wisdom'}
                </h4>
                <button
                  onClick={() => copyToClipboard(response.suggestion || response.content)}
                  className="p-1.5 rounded hover:bg-white/10 transition-colors"
                >
                  {copied ? <CheckIcon size={12} color="#4ADE80" /> : <CopyIcon size={12} color="rgba(255,255,255,0.4)" />}
                </button>
              </div>
              
              <p className="text-white/80 text-sm leading-relaxed">
                {response.suggestion || response.content}
              </p>

              {response.examples && response.examples.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-amber-500/20">
                  <p className="text-[10px] text-amber-400/60 uppercase tracking-wider">Examples:</p>
                  {response.examples.map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => applySuggestion(ex)}
                      className="block w-full text-left p-2 bg-white/5 rounded border border-white/10 hover:border-amber-500/30 transition-colors text-xs text-white/70 hover:text-white"
                    >
                      "{ex}"
                    </button>
                  ))}
                </div>
              )}

              {response.tip && (
                <p className="text-[10px] text-white/40 italic pt-2 border-t border-white/10">
                  💡 {response.tip}
                </p>
              )}
              
              {/* NVC Wisdom Quote */}
              {response.wisdom && (
                <div className="mt-3 pt-3 border-t border-amber-500/20 bg-amber-900/10 rounded-lg p-3">
                  <p className="text-[10px] text-amber-400/60 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <WisdomIcon size={10} color="#FBBF24" /> Wisdom from Marshall Rosenberg
                  </p>
                  <p className="text-xs text-amber-200/70 italic leading-relaxed">
                    "{response.wisdom}"
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Tiny Credits Link */}
        <div className="mt-4 pt-3 border-t border-white/5 text-center">
          <button
            onClick={() => {
              // Close wisdom guide first
              const event = new CustomEvent('openCredits');
              window.dispatchEvent(event);
            }}
            className="text-white/20 hover:text-white/40 text-[10px] underline underline-offset-2 transition-colors"
          >
            Credits
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Local fallback suggestions when endpoint is unavailable
const getLocalSuggestion = (queryType, ctx, input, query) => {
  // NVC Wisdom Quotes from Marshall Rosenberg
  const nvcWisdomQuotes = [
    "Belonging to Self: Enhancing compassion for yourself may be the single most important use of these principles.",
    "NVC helps us evaluate ourselves and others in a way that contributes to life, to growth, rather than to hatred and despair.",
    "Partially judging self, like all judgments, is a tragic expression of unmet needs.",
    "To mourn and to belong is to fully connect with the unmet needs and feelings generated when we have been less than perfect—without blaming or hating ourselves.",
    "Connecting with the need we were trying to meet when we took the action that led to regret enables self-forgiveness.",
    "With every choice you make, be conscious of what needs it serves.",
    "When we become aware of our needs, anger gives way to life-serving feelings.",
    "When hearing a difficult message, we have four options: 1) Blame ourselves 2) Blame others 3) Sense our own feelings and needs 4) Sense others' feelings and needs.",
    "Judgment of others contributes to self-fulfilling prophecies.",
    "Learn to hear needs regardless of how people express them.",
    "No matter how right you are, be relentless in your empathy until the person is fully heard. This is the only way they will actually hear you.",
    "People often need empathy before they can hear what is being said. Trying to educate or criticize makes things worse—they are not hearing you.",
    "When one party is in too much pain to hear the needs of the other, extend empathy relentlessly. Keep going, taking as long as necessary to ensure their pain is heard.",
    "The intention behind the protective use of force is only to protect, not to punish, blame, or condemn.",
    "Compliments can be judgmental, even if positive, and therefore counterproductive to belonging.",
    "Express appreciation to celebrate, not to manipulate.",
    "Receive appreciation without feelings of superiority or false humility.",
  ];
  
  // Get a random wisdom quote
  const getRandomQuote = () => nvcWisdomQuotes[Math.floor(Math.random() * nvcWisdomQuotes.length)];
  
  const suggestions = {
    observe: {
      analyze: {
        title: 'Observation Reflection',
        suggestion: 'Good observations are specific and factual. Try to describe what a camera would record, without adding interpretations or judgments.',
        examples: [
          'When I saw the dishes in the sink...',
          'I noticed you came home at 9pm...',
          'During the meeting, I heard you say...'
        ],
        tip: 'Remove words like "always", "never", "should" - these are interpretations, not observations.',
        wisdom: getRandomQuote()
      },
      translate: {
        title: 'NVC Translation',
        suggestion: 'To translate to NVC, separate what happened from your story about it. Focus on concrete, observable facts.',
        examples: [
          'Instead of "You ignored me" - "When I said hello and didn\'t hear a response..."',
          'Instead of "You were late" - "When you arrived 20 minutes after our agreed time..."'
        ],
        wisdom: getRandomQuote()
      }
    },
    feel: {
      analyze: {
        title: 'Feelings Check',
        suggestion: 'Real feelings are internal experiences. Watch out for "faux feelings" that contain blame like "I feel abandoned" or "I feel attacked".',
        examples: [
          'Sad, hurt, disappointed, frustrated',
          'Anxious, worried, scared, nervous',
          'Happy, excited, grateful, relieved'
        ],
        tip: '"I feel that..." is usually a thought, not a feeling. Try "I feel [emotion] because..."',
        wisdom: getRandomQuote()
      },
      identify: {
        title: 'Feeling Identification',
        suggestion: 'Connect with your body. Where do you feel tension or sensation? That often points to the underlying emotion.',
        examples: [
          'Tightness in chest - anxiety, fear, sadness',
          'Heat in face - embarrassment, anger, shame',
          'Heaviness - grief, disappointment, exhaustion'
        ],
        wisdom: getRandomQuote()
      }
    },
    need: {
      analyze: {
        title: 'Needs Reflection',
        suggestion: 'Universal needs are shared by all humans. They\'re different from strategies (specific ways to meet needs).',
        examples: [
          'Connection, belonging, love, acceptance',
          'Autonomy, freedom, choice, independence',
          'Safety, security, trust, stability'
        ],
        tip: 'If it involves a specific person or action, it\'s a strategy, not a need.',
        wisdom: getRandomQuote()
      },
      identify: {
        title: 'Need Identification',
        suggestion: 'Behind every feeling is a need. Ask yourself: "What am I longing for right now?"',
        examples: [
          'Feeling lonely - need for connection',
          'Feeling anxious - need for safety/predictability',
          'Feeling frustrated - need for understanding/efficiency'
        ],
        wisdom: getRandomQuote()
      }
    },
    default: {
      title: 'NVC Guidance',
      suggestion: 'Remember: the goal isn\'t to be perfect at NVC. It\'s to create genuine connection and find solutions where everyone wins.',
      examples: [],
      tip: 'When in doubt, lead with empathy. Try to understand the other person before asking to be understood.',
      wisdom: getRandomQuote()
    }
  };

  const stepKey = ctx.focus === 'observations' ? 'observe' : 
                  ctx.focus === 'feelings' ? 'feel' : 
                  ctx.focus === 'needs' ? 'need' : 'default';
  
  return suggestions[stepKey]?.[queryType] || suggestions[stepKey]?.analyze || suggestions.default;
};

export default WisdomGuide;
